import { useState, useEffect, useMemo } from "react";
import { authFetch } from "../../App";
import { toast } from "sonner";
import {
  Search, ChevronRight, ChevronDown, Check, Trash2, FolderOpen,
  Loader2, ArrowLeft, Building2, Hash, Sparkles, FileText, Layers,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API = "/api";
const fmt = (n) => n != null ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "\u2014";

export function LVViewer({ data, onAdopt, onDiscard, onBack }) {
  const [search, setSearch] = useState("");
  const [expandedTexts, setExpandedTexts] = useState(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [adopting, setAdopting] = useState(false);
  const [showAdoptDialog, setShowAdoptDialog] = useState(false);
  const [projects, setProjects] = useState([]);
  const [adoptMode, setAdoptMode] = useState("new");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [blockName, setBlockName] = useState(data.lv_desc || data.lv_name || "");
  const [newProjectName, setNewProjectName] = useState(data.project_desc || data.lv_desc || "");

  useEffect(() => {
    if (showAdoptDialog) {
      authFetch(`${API}/projects`).then(r => r.ok ? r.json() : []).then(setProjects);
    }
  }, [showAdoptDialog]);

  const structure = data.lv_structure || [];
  const hasStructure = structure.length > 0;

  const getKurztext = (item) => {
    if (item.kurztext) return item.kurztext;
    if (item.text) return item.text.split("\n")[0].trim();
    return "";
  };

  const getLangtext = (item) => {
    if (!item.text) return "";
    return item.text;
  };

  const toggleText = (id) => {
    setExpandedTexts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const allIds = new Set();
    for (const item of structure) {
      if ((item.type === "position" || item.type === "hinweis") && item.text && item.text.length > 0) {
        allIds.add(item.id);
      }
    }
    setExpandedTexts(allIds);
  };

  const collapseAll = () => setExpandedTexts(new Set());

  const filteredStructure = useMemo(() => {
    if (!search.trim() || !hasStructure) return structure;
    const q = search.toLowerCase();
    const matchIds = new Set();
    const parentOzs = new Set();
    for (const item of structure) {
      if (item.type === "position" || item.type === "hinweis") {
        const searchable = ((item.text || "") + " " + (item.kurztext || "") + " " + (item.oz_display || "")).toLowerCase();
        if (searchable.includes(q)) {
          matchIds.add(item.id);
          if (item.oz) {
            parentOzs.add(item.oz.substring(0, 2));
            if (item.oz.length >= 4) parentOzs.add(item.oz.substring(0, 4));
          }
        }
      }
    }
    return structure.filter(item => {
      if (item.type === "position" || item.type === "hinweis") return matchIds.has(item.id);
      if (item.type === "gruppe") return parentOzs.has(item.oz);
      return false;
    });
  }, [structure, search, hasStructure]);

  const legacyGroups = useMemo(() => {
    if (hasStructure) return [];
    const g = {};
    for (const pos of data.positions || []) {
      const key = `${pos.kg}.${pos.titel}`;
      if (!g[key]) g[key] = { kg: pos.kg, titel: pos.titel, positions: [], relevant: 0 };
      g[key].positions.push(pos);
      if (pos.auto_relevant) g[key].relevant++;
    }
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data, hasStructure]);

  const toggleCollapse = (oz) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(oz)) next.delete(oz); else next.add(oz);
      return next;
    });
  };

  const handleAdopt = async () => {
    setAdopting(true);
    try {
      const body = { block_name: blockName || undefined };
      if (adoptMode === "existing" && selectedProjectId) {
        body.project_id = selectedProjectId;
      } else {
        body.project_name = newProjectName;
      }
      const res = await authFetch(`${API}/gaeb/imports/${data.id}/adopt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Block "${result.block_name}" hinzugefuegt`);
        onAdopt(result.project_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler"); }
    setAdopting(false);
    setShowAdoptDialog(false);
  };

  const totalPos = data.positions?.length || 0;
  const relevantPos = data.positions?.filter((p) => p.auto_relevant).length || 0;
  const gruppenCount = structure.filter(s => s.type === "gruppe").length;
  const hinweisCount = structure.filter(s => s.type === "hinweis").length;

  const TypeBadge = ({ type }) => {
    const styles = { gruppe: "bg-blue-100 text-blue-700", hinweis: "bg-purple-100 text-purple-700", position: "bg-gray-100 text-gray-600" };
    const labels = { gruppe: "Gruppe", hinweis: "Hinweis", position: "Position" };
    return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${styles[type] || "bg-gray-100 text-gray-600"}`}>{labels[type] || type}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="gaeb-viewer-back"><ArrowLeft className="w-4 h-4" /></button>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900 truncate">{data.lv_desc || data.filename}</h2>
          <p className="text-xs text-gray-500">{data.project_desc} &mdash; {data.client_name}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { icon: Hash, label: "Positionen", value: totalPos },
          { icon: Layers, label: "Gruppen", value: gruppenCount },
          { icon: FileText, label: "Hinweise", value: hinweisCount },
          { icon: Sparkles, label: "Relevant", value: relevantPos },
          { icon: Building2, label: "AG", value: data.client_name || "\u2014" },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1"><Icon className="w-3 h-3" /><span className="text-[10px] uppercase tracking-wide">{label}</span></div>
            <p className="text-sm font-semibold text-gray-800 truncate">{value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={() => setShowAdoptDialog(true)} className="gap-2 bg-green-600 hover:bg-green-700" data-testid="gaeb-adopt-btn">
          <FolderOpen className="w-4 h-4" /> In Projekt uebernehmen
        </Button>
        {!data.adopted_projects?.length && (
          <Button variant="outline" onClick={onDiscard} className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="gaeb-discard-btn">
            <Trash2 className="w-4 h-4" /> Verwerfen
          </Button>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={expandAll} className="gap-1 text-[11px] h-7" data-testid="langtext-expand-all">
            <ChevronDown className="w-3 h-3" /> Alle Langtexte
          </Button>
          <Button size="sm" variant="outline" onClick={collapseAll} className="gap-1 text-[11px] h-7" data-testid="langtext-collapse-all">
            <ChevronRight className="w-3 h-3" /> Einklappen
          </Button>
        </div>
      </div>

      {data.adopted_projects?.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <p className="text-sm text-green-800 font-medium flex items-center gap-1.5 mb-2">
            <Check className="w-4 h-4" /> {data.adopted_projects.length}x in Projekt uebernommen
          </p>
          <div className="flex flex-wrap gap-2">
            {data.adopted_projects.map((ap) => (
              <button key={ap.project_id} onClick={() => onAdopt(ap.project_id)} className="text-xs bg-white border border-green-200 text-green-700 px-2.5 py-1 rounded-full hover:bg-green-100 transition-colors font-medium" data-testid={`adopted-project-link-${ap.project_id}`}>
                {ap.project_name} &rarr;
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Adopt Dialog */}
      {showAdoptDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdoptDialog(false)} data-testid="adopt-dialog-overlay">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="adopt-dialog">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">GAEB in Projekt uebernehmen</h3>
              <p className="text-xs text-gray-500 mt-1">Alle {totalPos} Positionen werden 1:1 uebernommen</p>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button onClick={() => setAdoptMode("new")} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${adoptMode === "new" ? "bg-white shadow text-gray-900" : "text-gray-500"}`} data-testid="adopt-mode-new">Neues Projekt</button>
                <button onClick={() => setAdoptMode("existing")} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${adoptMode === "existing" ? "bg-white shadow text-gray-900" : "text-gray-500"}`} data-testid="adopt-mode-existing">Bestehendes Projekt</button>
              </div>
              {adoptMode === "new" && (<div><label className="text-xs font-medium text-gray-600 mb-1 block">Projektname</label><input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="adopt-project-name" /></div>)}
              {adoptMode === "existing" && (<div><label className="text-xs font-medium text-gray-600 mb-1 block">Projekt waehlen</label><select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 bg-white" data-testid="adopt-project-select"><option value="">-- Projekt waehlen --</option>{projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}</select></div>)}
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Block-Bezeichnung</label><input value={blockName} onChange={(e) => setBlockName(e.target.value)} placeholder="z.B. Haupt-LV, Beleuchtung, Nachtrag 1" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="adopt-block-name" /><p className="text-[11px] text-gray-400 mt-1">Wird als separater Block im Projekt angezeigt</p></div>
            </div>
            <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdoptDialog(false)} className="text-sm" data-testid="adopt-cancel">Abbrechen</Button>
              <Button onClick={handleAdopt} disabled={adopting || (adoptMode === "existing" && !selectedProjectId)} className="gap-2 bg-green-600 hover:bg-green-700 text-sm" data-testid="adopt-confirm">
                {adopting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Uebernehmen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suchen (OZ, Text, Beschreibung...)" className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="gaeb-search" />
      </div>

      {/* Hierarchical LV Structure */}
      {hasStructure ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="gaeb-lv-tree">
          <div className="grid grid-cols-[70px_90px_1fr_80px_50px] gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-400 font-semibold sticky top-0 z-10">
            <span>Typ</span><span>OZ</span><span>Kurztext / Langtext</span><span className="text-right">Menge</span><span>ME</span>
          </div>
          <div className="divide-y divide-gray-100">
            {(() => {
              let trackGroupOz = "";
              return filteredStructure.map((item) => {
                if (item.type === "gruppe") trackGroupOz = item.oz;
                const parentCollapsed = item.type !== "gruppe" && [...collapsedGroups].some(oz =>
                  (item.type === "position" && item.oz && item.oz.startsWith(oz)) ||
                  (item.type === "hinweis" && trackGroupOz && (trackGroupOz === oz || trackGroupOz.startsWith(oz)))
                );
                if (parentCollapsed) return null;
                if (item.type === "gruppe" && item.level > 1) {
                  const parentOz = item.oz.substring(0, (item.level - 1) * 2);
                  if (collapsedGroups.has(parentOz)) return null;
                }
                const indent = (item.level || 0) * 16;
                const isExpanded = expandedTexts.has(item.id);

                if (item.type === "gruppe") {
                  const isCollapsed = collapsedGroups.has(item.oz);
                  return (
                    <div key={item.id} className={`grid grid-cols-[70px_90px_1fr_80px_50px] gap-2 px-4 py-2.5 items-center cursor-pointer hover:bg-blue-50/50 transition-colors ${item.level === 1 ? "bg-blue-50/70 border-l-[3px] border-l-blue-400" : "bg-blue-50/30 border-l-[3px] border-l-blue-200"}`} onClick={() => toggleCollapse(item.oz)} data-testid={`gaeb-group-${item.oz}`}>
                      <TypeBadge type="gruppe" />
                      <span className="text-xs font-mono font-bold text-blue-800">{item.oz_display}</span>
                      <div className="flex items-center gap-2 min-w-0" style={{ paddingLeft: indent }}>
                        {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-blue-400 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
                        <span className="text-[13px] font-semibold text-blue-900">{item.kurztext}</span>
                      </div>
                      <span /><span />
                    </div>
                  );
                }

                if (item.type === "hinweis") {
                  const kurztext = getKurztext(item);
                  const langtext = getLangtext(item);
                  const hasLT = langtext && langtext.length > 0;
                  return (
                    <div key={item.id} className="bg-purple-50/40 border-l-[3px] border-l-purple-200" data-testid={`gaeb-hinweis-${item.id}`}>
                      <div className={`grid grid-cols-[70px_90px_1fr_80px_50px] gap-2 px-4 py-2 items-center ${hasLT ? "cursor-pointer hover:bg-purple-50/70" : ""}`} onClick={() => hasLT && toggleText(item.id)}>
                        <TypeBadge type="hinweis" />
                        <span />
                        <div className="flex items-center gap-2 min-w-0" style={{ paddingLeft: indent }}>
                          {hasLT && (isExpanded ? <ChevronDown className="w-3 h-3 text-purple-400 shrink-0" /> : <ChevronRight className="w-3 h-3 text-purple-400 shrink-0" />)}
                          <p className="text-[12px] font-medium text-purple-800">{kurztext}</p>
                        </div>
                        <span /><span />
                      </div>
                      {isExpanded && hasLT && (
                        <div className="px-4 pb-3 ml-[176px]" style={{ paddingLeft: indent }}>
                          <p className="text-[11px] text-purple-700/80 whitespace-pre-line leading-relaxed">{langtext}</p>
                        </div>
                      )}
                    </div>
                  );
                }

                const kurztext = getKurztext(item);
                const langtext = getLangtext(item);
                const hasLT = langtext && langtext !== kurztext && langtext.split("\n").length > 1;
                return (
                  <div key={item.id} className={`${item.auto_relevant ? "bg-amber-50/50 border-l-[3px] border-l-amber-400" : "border-l-[3px] border-l-transparent"}`} data-testid={`gaeb-pos-${item.oz_display || item.oz}`}>
                    <div className={`grid grid-cols-[70px_90px_1fr_80px_50px] gap-2 px-4 py-1.5 items-center ${hasLT ? "cursor-pointer hover:bg-gray-50" : ""}`} onClick={() => hasLT && toggleText(item.id)}>
                      <TypeBadge type="position" />
                      <span className="text-[11px] font-mono text-gray-500">{item.oz_display}</span>
                      <div className="flex items-center gap-2 min-w-0" style={{ paddingLeft: indent }}>
                        {hasLT && (isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" /> : <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" />)}
                        <p className="text-[12px] text-gray-700">{kurztext}</p>
                      </div>
                      <span className="text-right text-xs font-medium text-gray-600">{item.menge != null ? fmt(item.menge) : ""}</span>
                      <span className="text-[11px] text-gray-400">{item.einheit}</span>
                    </div>
                    {isExpanded && hasLT && (
                      <div className="px-4 pb-2 ml-[176px]" style={{ paddingLeft: indent }}>
                        <p className="text-[11px] text-gray-500 whitespace-pre-line leading-relaxed">{langtext}</p>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {legacyGroups.map(([key, grp]) => {
            const q = search.toLowerCase();
            const positions = q ? grp.positions.filter(p => (p.text || "").toLowerCase().includes(q) || (p.kurztext || "").toLowerCase().includes(q) || p.oz.includes(q)) : grp.positions;
            if (q && !positions.length) return null;
            const isOpen = !collapsedGroups.has(key);
            return (
              <div key={key}>
                <button onClick={() => toggleCollapse(key)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <span className="text-xs font-mono text-slate-500">{key}</span>
                  <span className="text-sm font-medium text-gray-700 flex-1 truncate">{(data.groups?.[key]?.label) || `Titel ${grp.titel}`}</span>
                  <span className="text-[10px] text-gray-400">{positions.length} Pos.</span>
                </button>
                {isOpen && positions.map((pos) => {
                  const isExp = expandedTexts.has(pos.id);
                  const hasLT = pos.text && pos.text.split("\n").length > 1;
                  return (
                    <div key={pos.id} className={`px-6 py-2 border-t border-gray-100/80 ${pos.auto_relevant ? "bg-amber-50/50" : ""} ${hasLT ? "cursor-pointer hover:bg-gray-50" : ""}`} onClick={() => hasLT && toggleText(pos.id)}>
                      <div className="flex items-start gap-3">
                        {hasLT && (isExp ? <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 mt-1" /> : <ChevronRight className="w-3 h-3 text-gray-400 shrink-0 mt-1" />)}
                        <span className="text-[11px] font-mono text-slate-400 shrink-0 mt-0.5">{pos.oz_display || pos.oz}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-medium text-gray-700">{pos.kurztext || pos.text?.split("\n")[0]}</p>
                          {isExp && pos.text && (<p className="text-[11px] text-gray-500 mt-1 whitespace-pre-line leading-relaxed">{pos.text}</p>)}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs font-medium text-gray-600">{pos.menge != null ? fmt(pos.menge) : "\u2014"} {pos.einheit}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
